import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import ConfirmationModal from "../../Common/ConfirmationModal";
import EditTeacherForm from "./EditTeacherForm";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const LINK_HOVER_COLOR = "#F7941D";

const Container = styled.div`
  background: #FFF;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0,0,0,0.05);
  padding: 1.5em 0em;
  clear: both;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1em 0;
  }

  @media (max-width: 480px) {
    padding: 0.75em 0;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  padding: 0em 1.5em;
  margin-bottom: 1em;
  label {
    font-size: 14px;
    margin-right: 8px;
    font-weight: bold;
    color: #000;
  }
  select, input {
    padding: 5px 10px;
    font-size: 12px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }
  input { width: 200px; background-color: "#fff"}

  @media (max-width: 900px) {
    gap: 16px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding: 0 0.75em;

    div {
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
    }

    label {
      margin-right: 0;
    }

    select,
    input {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    padding: 0 0.5em;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  ${adminTable};
  min-width: 720px;

  th.startDateColumn,
  td.startDateColumn {
    width: 80px;
    min-width: 80px;
  }

  th.startDateColumn .dateHeaderLine {
    display: block;
    white-space: nowrap;
  }

  td.startDateColumn .dateLine {
    display: block;
    white-space: nowrap;
  }

  th.schoolColumn,
  td.schoolColumn {
    width: 200px;
    max-width: 250px;
  }

  td.schoolColumn {
    white-space: normal;
    word-break: break-word;
  }

  th.educatorColumn,
  td.educatorColumn {
    min-width: 320px;
  }

  @media (max-width: 1024px) {
    th.startDateColumn,
    td.startDateColumn {
      width: 80px;
      min-width: 80px;
    }

    th.schoolColumn,
    td.schoolColumn {
      width: 170px;
      max-width: 170px;
    }

    th.educatorColumn,
    td.educatorColumn {
      min-width: 320px;
    }
  }

  @media (max-width: 768px) {
    th.educatorColumn,
    td.educatorColumn {
      min-width: initial;
    }
  }

  thead th {
    ${adminTableHeaderCell};
    position: relative;
    background-color: #fff;
    .sort-icons {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;
      span {
        cursor: pointer;
        line-height: 10px;
        color: #9CA3AF;
        display: block;
      }
      .active { color: #A855F7; }
    }
  }

  tbody td {
    ${adminTableDataCell};
    padding: 12px 12px;
    vertical-align: top;
  }

  .LinkButton {
    text-decoration: none;
    line-height: 20px;
    font-size: 12px;
    display: inline-block;
    margin-top: 5px;
    padding: 6px 12px;
    margin-right: 0.5em;
    border-radius: 3px;
    cursor: pointer;
    color: #fff;
    background: ${({ colors }) => colors.buttonBackground};
    transition: background 0.2s ease, color 0.2s ease;
    font-weight: bold;

    &:hover {
      background: ${LINK_HOVER_COLOR};
    }
  }
`;

const StatusNotice = styled.div`
  font-size: 19.5px;
  margin-bottom: 1em;
  padding: 0.75em 1em;
  border-radius: 4px;
  background: transparent;
  color: red;
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 10px;
  gap: 0.5em;

  @media (max-width: 768px) {
    padding-right: 0.5em;
  }

  @media (max-width: 480px) {
    padding-right: 0.25em;
  }

  button {
    padding: 5px 10px;
    border: 1px solid ${({ colors }) => colors.border};
    background: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;
    &:disabled {
      background: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;
const SpinnerWrapper = styled.td`
  text-align: center;
  padding: 2em;
  justify-content: center;
  align-items: center;
`;
const Spinner = styled.div`
  display: inline-block;
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  width: 24px; height: 24px;
  justify-content: center;
  align-items: center;
  animation: ${spin} 1s linear infinite;
`;

const gradeOrder = code =>
  code === "K" ? 0 : isNaN(+code) ? 13 : +code;

const getSubscriptionStatus = (startDate, endDate) => {
  if (!startDate || !endDate) return "unknown";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "unknown";
  }
  if (today < start) return "future";
  if (today > end) return "past";
  return "current";
};

const STATUS_MESSAGES = {
  past: "You are viewing IPs associated with a past invoice.",
  future: "You are viewing IPs associated with a Future invoice.",
};

export default function TeacherList({
  data,
  total,
  colors,
  itemsPerPage, setItemsPerPage,
  page, setPage,
  searchTerm, setSearchTerm,
  sortKey, sortOrder, setSortKey, setSortOrder,
  onTeacherUpdated,
  onShowDetails,
  loading,
}) {
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [confirmOpen, setConfirmOpen]       = useState(false);
  const [confirmTitle, setConfirmTitle]     = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [resultStatus, setResultStatus] = useState("Success");
  const [confirmAction, setConfirmAction]   = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [resultOpen, setResultOpen]         = useState(false);
  const selectedSubscription =
    useSelector((s) => s.auth.selected_subscriptions) || {};
  const subscriptionStatus = getSubscriptionStatus(
    selectedSubscription.start_date || selectedSubscription.start,
    selectedSubscription.end_date || selectedSubscription.end
  );
  const statusMessage = STATUS_MESSAGES[subscriptionStatus] || "";

  // total pages & display range
  const pageCount = itemsPerPage > 0
    ? Math.ceil(total / itemsPerPage)
    : 1;
  const start = total > 0
    ? (page - 1) * itemsPerPage + 1
    : 0;
  const end = start + data.length - 1;

  function handleSort(k) {
    if (sortKey === k) setSortOrder(o => (o === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortOrder("asc"); }
    setPage(1);
  }

  function openEdit(t) {
    setEditingTeacher(t);
  }

  function buildPayloadFor(t, action) {
    if (action === "remove") {
      return {
        customer_user_id: t.customer_user_id,
        implementation_id: t.implementation_id,
        user_id: t.user_id
      };
    }
   if (action === "deny") {
      // when toggling access, send deny=1 to revoke, deny=0 to allow
      return {
        customer_user_id: t.customer_user_id,
        deny: t.access_revoked === "1" ? 0 : 1
      };
    }
    if (action === "certify" || action === "uncertify") {
      return { implementation_id: t.implementation_id };
    }
  }

  function handleAction(t, action, title, message) {
    setPendingPayload(buildPayloadFor(t, action));
    setConfirmAction(action);
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmOpen(true);
  }

  function handleConfirm() {
    setConfirmOpen(false);
    setResultStatus("Success");
    let call;
    switch (confirmAction) {
      case "remove":
        call = API.removeSubscriptionTeacher(pendingPayload);
        break;
      case "deny":
        call = API.denySubscriptionTeacher(pendingPayload);
        break;
      case "certify":
        call = API.certifySubscriptionTeacher(pendingPayload);
        break;
      case "uncertify":
        call = API.uncertifySubscriptionTeacher(pendingPayload);
        break;
      default:
        return;
    }
    call
      .then(resp => {
        const status = resp?.status === "Failed" ? "Failed" : "Success";
        setConfirmTitle(status === "Failed" ? "Error" : "Success");
        setConfirmMessage(resp.message || (status === "Failed" ? "Failed." : "Done."));
        setResultStatus(status);
        setResultOpen(true);
      })
      .catch(err => {
        setConfirmTitle("Error");
        setConfirmMessage(err.message || "Failed.");
        setResultStatus("Failed");
        setResultOpen(true);
      })
      .finally(() => setEditingTeacher(null));
  }

  function handleResultClose() {
    setResultOpen(false);
    setResultStatus("Success");
    setEditingTeacher(null);
    onTeacherUpdated && onTeacherUpdated();
  }

  return (
    <Container>
      {statusMessage && (
        <StatusNotice
          colors={colors}
          statusType={subscriptionStatus}
        >
          {statusMessage}
        </StatusNotice>
      )}
      <Controls colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(+e.target.value);
              setPage(1);
            }}
          >
            {[10,25,50,100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value={-1}>All</option>
          </select>
          <label style={{ marginLeft: 10 }}>entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            placeholder="Type to filter…"
            style={{ width: 400, backgroundColor: '#fff' }}
            value={searchTerm}
            onChange={e=>{ setSearchTerm(e.target.value); setPage(1); }}
          />
        </div>
      </Controls>

      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              {[
                {
                  key: "start_date",
                  label: (
                    <>
                      <span className="dateHeaderLine">Start Date</span>
                      <span className="dateHeaderLine">End Date</span>
                    </>
                  ),
                  headerClass: "startDateColumn"
                },
                {key:"school_name",label:"School Name", headerClass:"schoolColumn"},
                {key:"educator_name",label:"Educator", headerClass:"educatorColumn"},
                {key:"students_count",label:"Students/Grades*"},
              ].map(col => {
                const isAscActive = sortKey === col.key && sortOrder === "asc";
                const isDescActive = sortKey === col.key && sortOrder === "desc";
                const defaultColor = "#9CA3AF";
                const activeColor = "#A855F7";
                return (
                  <th key={col.key} className={col.headerClass || ""}>
                    {col.label}
                    <div className="sort-icons">
                      <span
                        onClick={() => handleSort(col.key)}
                        className={isAscActive ? "active" : ""}
                        style={{
                          color: isAscActive ? activeColor : defaultColor,
                          display: isDescActive ? "none" : "block"
                        }}
                      >
                        ▲
                      </span>
                      <span
                        onClick={() => handleSort(col.key)}
                        className={isDescActive ? "active" : ""}
                        style={{
                          color: isDescActive ? activeColor : defaultColor,
                          display: isAscActive ? "none" : "block"
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <SpinnerWrapper colSpan={4}>
                  <Spinner colors={colors}/>
                </SpinnerWrapper>
              </tr>
            ) : data.length > 0 ? (
              data?.map((t,i) => {
                const disabled = !t.implementation_id;
                const btnStyle = {
                  fontWeight: "bold",
                  opacity: disabled ? 0.5 : 1,
                  cursor: disabled ? "not-allowed" : "pointer"
                };
                return (
                  <tr key={i}>
                    <td className="startDateColumn">
                      <span className="dateLine">{t.start_date}</span>
                      <span className="dateLine">{t.end_date}</span>
                    </td>
                    <td className="schoolColumn">{t.school_name||t.org_name}</td>
                    <td className="educatorColumn">
                      <a
                        style={{ color: colors.buttonBackground, cursor: "pointer" }}
                        onClick={() => onShowDetails(t)}
                      >
                        <strong>
                          {t.name}
                        </strong>
                      </a>{" "}
                      <a  style={{ color: colors.buttonBackground, cursor: "pointer",  textDecoration: 'none'}} href={`mailto:${t.email}`}>{t.email}</a><br/>

                      <span
                        className="LinkButton"
                        style={btnStyle}
                        onClick={() => !disabled && handleAction(t, "remove", "Confirm Remove", "Remove this implementation?")}
                      >Remove</span>

                      <span
                        className="LinkButton"
                        style={btnStyle}
                        onClick={() => !disabled && handleAction(t,"deny",t.access_revoked === "1" ? "Confirm Allow Access" : "Confirm Deny Access", t.access_revoked === "1"? "Allow access to this educator?": "Deny access to this educator?")} >
                    {t.access_revoked === "1" ? "Allow Access" : "Deny Access"}
                  </span>

                      <span
                        className="LinkButton"
                        style={btnStyle}
                        onClick={() => openEdit(t)}
                      >Edit</span>

                      <span
                        className="LinkButton"
                        style={btnStyle}
                        onClick={() => !disabled && handleAction(
                          t,
                          t.certified ? "uncertify" : "certify",
                          t.certified ? "Confirm Uncertify" : "Confirm Certify",
                          t.certified ? "Remove certification?" : "Certify this educator?"
                        )}
                      >
                        {t.certified ? "Uncertify" : "Certify"}
                      </span>
                    </td>
                    <td>
                      {t.students_count}<br/>
                      {(() => {
                        const segments = t.grade_records
                          .sort((a, b) => gradeOrder(a.grade_code) - gradeOrder(b.grade_code))
                          .reduce((list, record) => {
                            const isStatusComplete = Number(record.status) === 1;
                            const downloaded = Number(record.downloaded) === 1;
                            const style = isStatusComplete
                              ? "bold"
                              : downloaded
                                ? "underline"
                                : "italic";
                            const last = list[list.length - 1];
                            if (last && last.style === style) {
                              last.codes.push(record.grade_code);
                            } else {
                              list.push({ style, codes: [record.grade_code] });
                            }
                            return list;
                          }, []);

                        return segments.map((segment, idx) => {
                          const commonText = segment.codes.join(" ");
                          let content;
                          if (segment.style === "bold") {
                            content = (
                              <span style={{ fontWeight: "bold", fontStyle: "italic" }}>
                                {commonText}
                              </span>
                            );
                          } else if (segment.style === "underline") {
                            content = (
                              <span style={{ textDecoration: "underline", fontStyle: "italic" }}>
                                {commonText}
                              </span>
                            );
                          } else {
                            content = (
                              <span style={{ fontStyle: "italic" }}>
                                {commonText}
                              </span>
                            );
                          }

                          return (
                            <React.Fragment key={`${segment.style}-${idx}`}>
                              {content}
                              {idx < segments.length - 1 ? " " : null}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={4} style={{ textAlign:"center", padding:"1em" }}>
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </TableWrapper>

      <InfoText colors={colors}>
        Showing {start} to {end} of {total} entries
      </InfoText>

      <Pagination colors={colors}>
        <button
          onClick={() => setPage(p => Math.max(p - 1, 1))}
          disabled={page === 1}
        >Previous</button>
        <button
          onClick={() => setPage(p => Math.min(p + 1, pageCount))}
          disabled={page === pageCount}
        >Next</button>
      </Pagination>

      {editingTeacher && (
        <EditTeacherForm
          teacher={editingTeacher}
          colors={colors}
          onCancel={() => setEditingTeacher(null)}
          onSave={handleResultClose}
        />
      )}

      <ConfirmationModal
        show={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      <ConfirmationModal
        show={resultOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleResultClose}
        status={resultStatus}
        single
      />
    </Container>
  );
}
